import React, { useMemo, useReducer } from 'react';
import PropTypes from 'prop-types';
import { Stack, Alert, Typography, ReactDataTable, useFormContext } from '@metarouter-private/mr-mui';

import useFeatureEnabled from 'app/launch-darkly/use-feature-enabled';
import { FEATURE_FLAGS } from 'app/launch-darkly/featureFlags';
import { useConsentModeEnabled } from 'app/components/consent-mode-select/GatedConsentModeSelect';

import { useConsentCategorySelectEnabled } from './shared/consent-category/GatedConsentCategorySelect';
import buildConfirmUpdateColumns from './buildConfirmUpdateColumns';
import PipelineFormPanel from './PipelineFormPanel';
import ConfirmUpdateToolbar from './ConfirmUpdateToolbar';
import { getIncompletePipelineIds, getRequiredVariableNames } from './confirmUpdateFormModel';

const CONSENT_DISABLED_MESSAGE =
  'Consent Enforcement has not been enabled for your organization, but all your events will be captured and processed. Please reach out to your customer success representative for more information.';

export default function ConfirmUpdateContent({
  selectedPipelines,
  targetPlaybook,
  integrationId,
  pipelineCountMessage,
  showOnlyIncomplete,
  onToggleShowOnlyIncomplete,
}) {
  const { getValues } = useFormContext();

  const { configurableConsentMvpEnabled, ecmConfigurableConsentEnabled } = useFeatureEnabled(
    FEATURE_FLAGS.configurableConsentMvp,
    FEATURE_FLAGS.ecmConfigurableConsent
  );

  const { consentModeEnabled } = useConsentModeEnabled();
  // Share the modal's hook so the two can't drift on what "category enabled" means.
  const { consentCategorySelectEnabled: consentCategoryEnabled } = useConsentCategorySelectEnabled();

  // Raw flags here: the alert fires on the in-between state (consent on, enforcement off).
  const showConsentDisabledAlert = configurableConsentMvpEnabled && !ecmConfigurableConsentEnabled;

  // The filtered `data` below is a snapshot recomputed each render from
  // getValues(); a bulk-apply mutates the form via reset(), which does NOT
  // re-render this component (it doesn't subscribe to values). Bump this so a
  // bulk-apply recomputes the snapshot, keeping the no-useWatch semantics.
  const [, forceFilterRefresh] = useReducer(refreshCount => refreshCount + 1, 0);

  const pipelineVariableNames = useMemo(() => getRequiredVariableNames(targetPlaybook), [targetPlaybook]);

  const hasPipelineVariables = pipelineVariableNames.length > 0;

  // Each row carries a top-level `id` (RDT keyField) so expand/identity stays
  // stable across paginate/filter.
  const rows = useMemo(() => {
    return selectedPipelines.map(pipeline => {
      // pipelineIntegrations uses the 'revision' alias for staged_revision (see useLazyQueryIonPipelines).
      const existingPipelineIntegration = pipeline.pipelineIntegrations?.find(
        pipelineIntegration => pipelineIntegration.integrationId === integrationId
      );

      const currentVars = existingPipelineIntegration?.revision?.pipelineVars || {};

      const pipelineVariables = pipelineVariableNames.map(name => ({
        name,
        currentValue: currentVars[name] || '',
      }));

      const currentRevision = existingPipelineIntegration?.revision?.playbook?.revision;

      // GatedConsentFields defaults mode to ACE when null/undefined.
      const currentConsentCategoryId = existingPipelineIntegration?.consent?.consentCategoryId || '';
      const currentConsentMode = existingPipelineIntegration?.consent?.mode;

      return {
        id: pipeline.id,
        pipeline,
        pipelineVariables,
        currentRevision,
        currentConsentCategoryId,
        currentConsentMode,
      };
    });
  }, [selectedPipelines, integrationId, pipelineVariableNames]);

  const columns = useMemo(
    () =>
      buildConfirmUpdateColumns({
        targetRevision: targetPlaybook.revision,
        requiredVariableNames: pipelineVariableNames,
        consentCategoryEnabled,
      }),
    [targetPlaybook.revision, pipelineVariableNames, consentCategoryEnabled]
  );

  // Snapshot derivation, deliberately NOT a useWatch/useMemo over form values:
  // the incomplete set is recomputed only on discrete re-renders (toggling the
  // checkbox, a submit attempt), not on every keystroke, to avoid an O(N) scan
  // of the whole selection per character typed. A row fixed after the snapshot
  // may linger in the filtered view until the next discrete render — accepted.
  let data = rows;
  if (showOnlyIncomplete) {
    const incompleteIds = new Set(
      getIncompletePipelineIds(getValues(), {
        pipelineIds: rows.map(row => row.id),
        requiredVariableNames: pipelineVariableNames,
        consentCategoryEnabled,
      })
    );
    data = rows.filter(row => incompleteIds.has(row.id));
  }

  const tableTitle = (
    <ConfirmUpdateToolbar
      showOnlyIncomplete={showOnlyIncomplete}
      onToggleShowOnlyIncomplete={onToggleShowOnlyIncomplete}
      consentCategoryEnabled={consentCategoryEnabled}
      consentModeEnabled={consentModeEnabled}
      variableNames={pipelineVariableNames}
      onApplied={forceFilterRefresh}
    />
  );

  return (
    <Stack spacing={2}>
      <Typography variant="body2" color="text.secondary">
        {pipelineCountMessage}
      </Typography>

      <Alert severity="info">
        {hasPipelineVariables
          ? 'Review and update pipeline variables for each pipeline below.'
          : 'No pipeline variables to configure for this playbook.'}
      </Alert>

      {showConsentDisabledAlert && <Alert severity="info">{CONSENT_DISABLED_MESSAGE}</Alert>}

      {/* Collapse-by-default expandable rows, client paginated so only the current
          page mounts at a time (RDT owns the expand state). */}
      <ReactDataTable
        title={tableTitle}
        columns={columns}
        data={data}
        keyField="id"
        pagination
        paginationPerPage={25}
        expandableRows
        expandableRowsComponent={PipelineFormPanel}
        fixedHeader
        fixedHeaderScrollHeight="55vh"
      />
    </Stack>
  );
}

ConfirmUpdateContent.propTypes = {
  selectedPipelines: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      name: PropTypes.string.isRequired,
      pipelineIntegrations: PropTypes.array,
    })
  ).isRequired,
  targetPlaybook: PropTypes.shape({
    id: PropTypes.string.isRequired,
    revision: PropTypes.number.isRequired,
    pipeline_variables: PropTypes.arrayOf(
      PropTypes.shape({
        name: PropTypes.string.isRequired,
      })
    ),
  }).isRequired,
  integrationId: PropTypes.string.isRequired,
  pipelineCountMessage: PropTypes.string.isRequired,
  showOnlyIncomplete: PropTypes.bool.isRequired,
  onToggleShowOnlyIncomplete: PropTypes.func.isRequired,
};
